import { useEffect, useRef, useState } from 'react';

import { useLiveQuery } from 'dexie-react-hooks';

import { DEXIE_IS_LOADING } from '../lib/db';
import { feedMessageById } from '../feed/echo/messageActions';

// Resolution + deletion signal for UI launched from a rendered feed row. The durable
// feed_messages row wins while it exists (so open surfaces stay live through edits);
// the retained renderer row keeps rendering when the id has left the durable cache.
// A feed row that disappears after it was seen means the message was deleted through
// a path that never dispatches the live-journal destroy event (e.g. reconnect sync
// after an offline delete on another device) — `deleted` lets each surface pick its
// terminal behavior: close, show an already-deleted state, or cancel edit mode.
//
// The signal is server-authoritative for the paths it was built on: reconcileAbsentMessages
// (rows the server omitted from a range it just returned) and the message.deleted sync
// event. It is an over-read for the paths that empty the table wholesale — prune(), which
// has no production caller and is documented as such on EchoDexieBuffer, and purgeWorkspace
// /purgeAccount, which do. Purge is tolerable only because its one caller is the workspace
// kill in live_journal_controller: that dims the page and immediately navigates away or
// resets the database, so a surface flashing "deleted" is never read. Anything else that
// clears feed_messages while a surface is open would report a lie; at that point deletion
// has to move to explicit events from the message.deleted write paths.
export function useRetainedFeedMessage(messageId, retainedRow = null) {
  const feedMessage = useLiveQuery(() => feedMessageById(messageId), [messageId], DEXIE_IS_LOADING);
  const [deleted, setDeleted] = useState(false);
  const feedRowSeenRef = useRef(false);

  useEffect(() => {
    feedRowSeenRef.current = false;
    setDeleted(false);
  }, [messageId]);

  useEffect(() => {
    if (feedMessage === DEXIE_IS_LOADING) return;
    if (feedMessage) {
      feedRowSeenRef.current = true;
      return;
    }
    if (feedRowSeenRef.current) setDeleted(true);
  }, [feedMessage]);

  const resolvedFeedMessage = feedMessage !== DEXIE_IS_LOADING && feedMessage ? feedMessage : null;

  return {
    message: resolvedFeedMessage ?? retainedRow ?? null,
    isLoading: feedMessage === DEXIE_IS_LOADING && !retainedRow,
    deleted,
  };
}
